import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  Grid,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  TableHead,
  Table,
  FormControl,
  TextField,
  Select,
  MenuItem,
  Autocomplete,
  Popper,
  Tooltip,
} from "@mui/material";
import React, { useState, useEffect, useMemo, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import {
  primaryBtnTeal,
  orangeButton,
  textfieldTextBlueBorder,
} from "../../style/PatientDetailsStyle";
import {
  generatePurchaseOrder,
  ExtractMedicineSummaryV1,
  generateMedicineSalt,
} from "../../connections/postApi";
import { uploadFileToS3 } from "../../connections/s3Upload";
import UseFetchSupplierList from "../../helper/UseFetchSupplierList";
import UseFetchPackList from "../../helper/UseFetchPackList";
import UseFetchLabInventories from "../../helper/UseFetchLabInventories";
import UseFetchMedicineAdmin from "../../helper/UseFetchMedicineAdmin";
import useDebounce from "../../hooks/UseDebounce";
import { showToast } from "../../utils/toastUtil";
import {
  autoFocusOffWithOutBorder,
  drTableGreen,
} from "../../style/PatinetFormStyle";
import {
  menuItemStyle,
  selectField,
  textfieldTextBlueBorderPharmacy,
} from "../../style/AppointmentStyle";
import { textfieldTextBlueBr } from "../../style/PharmacyStyle";
import { xBtn } from "../../style/LapPageStyle";
import AddCardIcon from "@mui/icons-material/AddCard";
import UseGetAllMedicineList from "../../helper/UseGetAllMedicineList";
import UseFetchFormType from "../../helper/UseFetchFormType";
import { getMedicineListBulkSearch, getMedicineSaltPharmacySearch } from "../../connections/getApi";
import { postBulkMedicine } from "../../connections/postApi";
import CreatableSelect from "react-select/creatable";
import UseGetAllMedicineSaltList from "../../helper/UseGetAllMedicineSaltList";
import { selectStyles } from "../../style/PatientDetailsStyle";
import {
  normalizeMedicineSalt,
  validateMedicineSalt,
} from "../../utils/medicineSaltUtils";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";

// ─── Helper: parse a date string (handles "Sep 18, 2026", ISO, etc.) ──────────
const parseToISODate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const parsed = new Date(dateStr);
    if (isNaN(parsed.getTime())) return "";
    return parsed.toISOString().split("T")[0]; // "YYYY-MM-DD"
  } catch {
    return "";
  }
};

// ─── Helper: is the date in the past? ─────────────────────────────────────────
const isDateInPast = (dateStr) => {
  if (!dateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  return d < today;
};

const AddPurchaseOrderFileScan = ({ onClose, refetch }) => {
  // ─── Step control ────────────────────────────────────────────────────────────
  const [step, setStep] = useState(1);

  // ─── Step 1 state ─────────────────────────────────────────────────────────
  const [uploadLoading, setUploadLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  };
  const [file, setFile] = useState(null);
  // ─── Step 2 — Purchase Order form state ───────────────────────────────────
  const [supplierInput, setSupplierInput] = useState(null);
  const [medicineSearchInput, setMedicineSearchInput] = useState(null);

  // Holds the full supplier object selected (either by AI name-match or manual)
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  // AI-returned supplier name (used for auto-matching after supplier list loads)
  const [aiSupplierName, setAiSupplierName] = useState("");

  const debouncedInputSupplier = useDebounce(supplierInput, 500);
  const debouncedInputMedicine = useDebounce(medicineSearchInput, 500);

  const { data: medicineList, isLoading: medicineLoading } =
    UseFetchMedicineAdmin(1, 10, debouncedInputMedicine);
  const { data: supplierList, isLoading: supplierListLoading } =
    UseFetchSupplierList(1, 10, debouncedInputSupplier);
  const { data: labInventoriesList } = UseFetchLabInventories();
  const { data: packList } = UseFetchPackList();
  const { data: formTypeList } = UseFetchFormType();

  const { data: medicineListAll } = UseGetAllMedicineList();

  const [filteredMedicines, setFilteredMedicines] = useState([]);
  const [fieldOptions, setFieldOptions] = useState({
    labInventoriesList:
      JSON.parse(localStorage.getItem("labInventoriesList")) || [],
    packList: JSON.parse(localStorage.getItem("packList")) || [],
  });

  const [formState, setFormState] = useState({
    supplier_id: "",
    suppliers: "",
    purchase_date: new Date().toISOString().split("T")[0],
    bill_type: "",
    bill_number: "",
    grand_total: "",
  });

  const [purchaseOrderItems, setPurchaseOrderItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [openMedicineList, setOpenMedicines] = useState(false);
  const [highlightedIndexMedicine, setHighlightedIndexMedicine] = useState(0);
  const [focus, setFocus] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [missingMedicines, setMissingMedicines] = useState([]);
  const [missingMedicinesLoading, setMissingMedicinesLoading] = useState(false);
  const [saltLoadingIndex, setSaltLoadingIndex] = useState(null);
  const [extraSaltOptions, setExtraSaltOptions] = useState([]);
  // Tracks the currently-typed salt text per row so it isn't lost on blur.
  const saltInputRef = useRef({});

  // ─── Sync medicine list ────────────────────────────────────────────────────
  useEffect(() => {
    setFilteredMedicines(medicineList?.payload?.medicines);
  }, [medicineList]);

  // ─── Sync supplier / pack / lab options ───────────────────────────────────
  useEffect(() => {
    if (supplierList?.payload?.suppliers?.length > 0) {
      const suppliers = supplierList.payload.suppliers;
      setFieldOptions((prev) => ({ ...prev, supplierList: suppliers }));

      // Auto-match AI supplier name once the list is available
      if (aiSupplierName && !selectedSupplier) {
        const match = suppliers.find(
          (s) =>
            s.name?.toLowerCase().trim() ===
            aiSupplierName?.toLowerCase().trim(),
        );
        if (match) {
          setSelectedSupplier(match);
          setFormState((prev) => ({
            ...prev,
            supplier_id: match.id,
            suppliers: match.id,
          }));
        }
      }
    }
    if (labInventoriesList?.payload?.lab_inventories?.length > 0) {
      localStorage.setItem(
        "labInventoriesList",
        JSON.stringify(labInventoriesList.payload.lab_inventories),
      );
      setFieldOptions((prev) => ({
        ...prev,
        labInventoriesList: labInventoriesList.payload.lab_inventories,
      }));
    }
    if (packList?.payload?.packing?.length > 0) {
      localStorage.setItem(
        "packList",
        JSON.stringify(packList.payload.packing),
      );
      setFieldOptions((prev) => ({
        ...prev,
        packList: packList.payload.packing,
      }));
    }
  }, [supplierList, labInventoriesList, packList, aiSupplierName]);

  // ─── Trigger supplier search with AI name so the list loads ───────────────
  useEffect(() => {
    if (aiSupplierName) {
      setSupplierInput(aiSupplierName);
    }
  }, [aiSupplierName]);

  // ─── Step 1: Upload & scan ─────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!file) return;
    try {
      setUploadLoading(true);
      const { key } = await uploadFileToS3(file, "GlobalAiContext", 0);
      const res = await ExtractMedicineSummaryV1(key, "purchase_order");

      const orderData = res?.payload?.summaries?.purchase_order;

      if (orderData) {
        // ── Get valid pack values from loaded packList ─────────────────────
        const validPacks = (fieldOptions?.packList || [])
          .filter(Boolean)
          .map((p) => p?.trim()?.toUpperCase());

        const resolvePackValue = (aiPack) => {
          if (!aiPack) return "";
          const normalized = aiPack.trim().toUpperCase();
          return validPacks.includes(normalized) ? aiPack.trim() : "";
        };

        // ── Map items ──────────────────────────────────────────────────────
        const mappedItems = orderData?.items
          ?.map((item) => {
            const rawExpiry = item?.expiry || item?.expiry_date || "";
            const isoExpiry = parseToISODate(rawExpiry);

            return {
              medicine_id: "",
              medicine_name: item?.medicine_name?.toUpperCase() || "",
              pack: resolvePackValue(item?.pack),
              hsn_code: item?.hsn_code || "",
              batch: item?.batch || "",
              expiry_date: isoExpiry,
              quantity: item?.quantity || "",
              free_quantity: item?.free_quantity || "",
              cgst: item?.cgst || "",
              sgst: item?.sgst || "",
              rate: item?.rate || "",
              mrp: item?.mrp || "",
              mfr: item?.mfr || "",
              discount: item?.discount || "",
              amount: "", // always recalculate, never trust AI amount
              form_type: item?.form_type || "",
              department_id: "9",
            };
          })
          .filter(Boolean);

        // ── Prefill top-level order fields ────────────────────────────────
        const purchaseDate = parseToISODate(
          orderData?.invoice_date || orderData?.purchase_date,
        );

        setFormState((prev) => ({
          ...prev,
          bill_number:
            orderData?.invoice_number ||
            orderData?.bill_number ||
            prev.bill_number,
          bill_type:
            orderData?.payment_type || orderData?.bill_type || prev.bill_type,
          purchase_date: purchaseDate || prev.purchase_date,
          grand_total:
            orderData?.total_amount ||
            orderData?.grand_total ||
            prev.grand_total,
        }));

        // ── Trigger supplier auto-match ───────────────────────────────────
        if (orderData?.supplier_name) {
          setAiSupplierName(orderData.supplier_name);
        }

        // ── Bulk-search scanned medicine names ────────────────────────────
        const medicineNames = mappedItems
          .map((item) => item.medicine_name)
          .filter(Boolean);

        let resolvedItems = mappedItems;

        // ── Always recalculate amount using our GST-inclusive formula ─────
        const recalculateAmount = (item) => {
          const rate = parseFloat(item.rate) || 0;
          const quantity = parseFloat(item.quantity) || 0;
          const discount = parseFloat(item.discount) || 0;
          const cgst = parseFloat(item.cgst) || 0;
          const sgst = parseFloat(item.sgst) || 0;

          if (!rate || !quantity) return { ...item, amount: "" };

          const taxableAmount = Math.max(rate * quantity - discount, 0);
          const cgstAmount = (taxableAmount * cgst) / 100;
          const sgstAmount = (taxableAmount * sgst) / 100;
          const finalAmount = taxableAmount + cgstAmount + sgstAmount;

          return { ...item, amount: finalAmount.toFixed(2) };
        };

        resolvedItems = resolvedItems.map(recalculateAmount);

        if (medicineNames.length > 0) {
          const searchResult = await getMedicineListBulkSearch(medicineNames);
          const existingMedicines = searchResult?.payload?.medicines || [];
          const existingNamesMap = new Map(
            existingMedicines.map((m) => [m.name?.toUpperCase().trim(), m]),
          );

          // Attach medicine_id for already-existing medicines
          resolvedItems = resolvedItems.map((item) => {
            const match = existingNamesMap.get(
              item.medicine_name?.toUpperCase().trim(),
            );
            return match
              ? {
                  ...item,
                  medicine_id: match.id,
                  form_type: item.form_type || match.form_type,
                }
              : item;
          });

          const missing = resolvedItems.filter(
            (item) =>
              !existingNamesMap.has(item.medicine_name?.toUpperCase().trim()),
          );

          // ── Recalculate grand total from our computed amounts ─────────
          const computedGrandTotal = resolvedItems
            .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0)
            .toFixed(2);

          setFormState((prev) => ({
            ...prev,
            grand_total: computedGrandTotal || prev.grand_total,
          }));

          if (missing.length > 0) {
            setMissingMedicines(
              missing.map((item) => ({
                medicine_name: item.medicine_name,
                form_type: item.form_type || "",
                medicine_salt: "",
                isNewSalt: false,
              })),
            );
            setPurchaseOrderItems(resolvedItems);
            setStep(2);
          } else {
            setPurchaseOrderItems(resolvedItems);
            setStep(3);
          }
        } else {
          // ── Recalculate grand total even when no medicine search needed ─
          const computedGrandTotal = resolvedItems
            .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0)
            .toFixed(2);

          setFormState((prev) => ({
            ...prev,
            grand_total: computedGrandTotal || prev.grand_total,
          }));

          setPurchaseOrderItems(resolvedItems);
          setStep(3);
        }
      } else {
        showToast("No Medicine found in scanned document", "ERROR");
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to scan document", "ERROR");
    } finally {
      setUploadLoading(false);
    }
  };

  // ─── Step 2: Missing medicines handlers ───────────────────────────────────

  const handleRemoveMissingMedicine = (index) => {
    const medicineName = missingMedicines[index]?.medicine_name;
    setMissingMedicines((prev) => prev.filter((_, i) => i !== index));
    setPurchaseOrderItems((prev) =>
      prev.filter(
        (item) =>
          item.medicine_name?.toUpperCase().trim() !==
          medicineName?.toUpperCase().trim(),
      ),
    );
  };

  const handleMissingMedicineChange = (index, field, value) => {
    setMissingMedicines((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[`${field}_${index}`];
      return updated;
    });
  };

  const handleAutoFillMissingSalt = async (index) => {
    const medicineName = missingMedicines[index]?.medicine_name?.trim();
    if (!medicineName) {
      showToast("Medicine name is required to auto-fill salt", "ERROR");
      return;
    }
    setSaltLoadingIndex(index);
    try {
      // Step 1: AI suggestion
      const res = await ExtractMedicineSummaryV1("", "medicine_salt", "GlobalAiContext", null, null, [medicineName]);
      const salt = res?.payload?.summaries?.medicines?.[0]?.salt;
      if (!salt) { showToast("Could not determine salt. Please enter manually.", "ERROR"); return; }
      const normalized = normalizeMedicineSalt(salt);

      // Step 2: search if salt already exists
      const searchRes = await getMedicineSaltPharmacySearch(normalized, 1, 5);
      const existing = searchRes?.payload?.medicine_salts || [];
      const match = existing.find((s) => s.name?.toLowerCase().trim() === normalized.toLowerCase().trim());

      if (match) {
        // Found — auto-select
        setExtraSaltOptions((prev) =>
          prev.some((o) => o.value === match.id) ? prev : [...prev, { value: match.id, label: match.name }]
        );
        handleMissingMedicineChange(index, "medicine_salt", match.name);
        setErrors((prev) => { const u = { ...prev }; delete u[`medicine_salt_${index}`]; return u; });
        showToast(`Salt "${match.name}" found & selected`, "SUCCESS");
      } else {
        // Not found — create then auto-select
        const result = await generateMedicineSalt({ medicine_salt: { name: normalized }, medicines_ids: [] });
        if (result?.status?.status === "SUCCESS") {
          const newSalt = result?.payload?.medicine_salt || result?.payload;
          const newId = newSalt?.id || null;
          const newName = newSalt?.name || normalized;
          setExtraSaltOptions((prev) =>
            prev.some((o) => o.value === newId) ? prev : [...prev, { value: newId || newName, label: newName }]
          );
          handleMissingMedicineChange(index, "medicine_salt", newName);
          setErrors((prev) => { const u = { ...prev }; delete u[`medicine_salt_${index}`]; return u; });
          showToast(result?.status?.message || "Salt created & selected", "SUCCESS");
        } else {
          showToast(result?.status?.message || "Failed to create salt", "ERROR");
        }
      }
    } catch {
      showToast("Failed to auto-fill salt", "ERROR");
    } finally {
      setSaltLoadingIndex(null);
    }
  };

  const handleCreateSaltAtIndex = async (inputValue, index) => {
    const normalized = normalizeMedicineSalt(inputValue);
    setSaltLoadingIndex(index);
    try {
      const result = await generateMedicineSalt({ medicine_salt: { name: normalized }, medicines_ids: [] });
      if (result?.status?.status === "SUCCESS") {
        const newSalt = result?.payload?.medicine_salt || result?.payload;
        const newId = newSalt?.id || null;
        const newName = newSalt?.name || normalized;
        setExtraSaltOptions((prev) =>
          prev.some((o) => o.value === newId) ? prev : [...prev, { value: newId || newName, label: newName }]
        );
        handleMissingMedicineChange(index, "medicine_salt", newName);
        setErrors((prev) => { const u = { ...prev }; delete u[`medicine_salt_${index}`]; return u; });
        showToast(result?.status?.message || "Salt created successfully", "SUCCESS");
      } else {
        showToast(result?.status?.message || "Failed to create salt", "ERROR");
      }
    } catch {
      showToast("Failed to create salt", "ERROR");
    } finally {
      setSaltLoadingIndex(null);
    }
  };

  const handleCreateMissingMedicines = async () => {
    if (missingMedicines.length === 0) {
      setStep(3);
      return;
    }

    const newErrors = {};
    missingMedicines.forEach((med, index) => {
      if (!med.medicine_name?.trim())
        newErrors[`medicine_name_${index}`] = "Medicine name is required";
      if (!med.form_type)
        newErrors[`form_type_${index}`] = "Form type is required";
      if (!med.medicine_salt?.trim())
        newErrors[`medicine_salt_${index}`] = "Medicine salt is required";
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showToast("Please fill all required fields for each medicine", "ERROR");
      return;
    }

    setMissingMedicinesLoading(true);
    try {
      const payload = {
        medicines: missingMedicines.map((med) => ({
          medicine_name: med.medicine_name.trim(),
          form_type: med.form_type || "",
          salt: med.medicine_salt || "",
        })),
      };
      const res = await postBulkMedicine(payload);

      // Toast for each medicine that failed to create
      if (res?.meta?.errors?.length > 0) {
        res.meta.errors.forEach((err) => {
          showToast(
            `Skipped "${err.medicine?.medicine_name}": ${err.error}`,
            "ERROR",
          );
        });
      }

      // Attach medicine_id for successfully created medicines
      if (res?.data?.length > 0) {
        const createdMap = new Map(
          res.data.map((m) => [m.name?.toUpperCase().trim(), m.id]),
        );
        setPurchaseOrderItems((prev) =>
          prev.map((item) => {
            const newId = createdMap.get(
              item.medicine_name?.toUpperCase().trim(),
            );
            return newId ? { ...item, medicine_id: newId } : item;
          }),
        );
      }

      setStep(3);
    } catch (err) {
      console.error(err);
      showToast("Failed to create medicines", "ERROR");
    } finally {
      setMissingMedicinesLoading(false);
    }
  };

  // ─── Step 3 helpers ────────────────────────────────────────────────────────

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleChange = (index, field, value) => {
    const updatedOrderArr = [...purchaseOrderItems];
    updatedOrderArr[index] = { ...updatedOrderArr[index], [field]: value };

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };

      if (updatedErrors[`${field}_${index}`]) {
        delete updatedErrors[`${field}_${index}`];
      }

      if (
        ["rate", "discount", "quantity", "cgst", "sgst"].includes(field) &&
        updatedErrors[`amount_${index}`]
      ) {
        delete updatedErrors[`amount_${index}`];
      }

      return updatedErrors;
    });

    if (field === "amount") {
      setPurchaseOrderItems(updatedOrderArr);
      return;
    }

    const rate = parseFloat(updatedOrderArr[index].rate) || 0;
    const quantity = parseFloat(updatedOrderArr[index].quantity) || 0;
    const discount = parseFloat(updatedOrderArr[index].discount) || 0;
    const cgst = parseFloat(updatedOrderArr[index].cgst) || 0;
    const sgst = parseFloat(updatedOrderArr[index].sgst) || 0;

    const taxableAmount = Math.max(rate * quantity - discount, 0);
    const cgstAmount = (taxableAmount * cgst) / 100;
    const sgstAmount = (taxableAmount * sgst) / 100;
    const finalAmount = taxableAmount + cgstAmount + sgstAmount;

    updatedOrderArr[index].amount = finalAmount.toFixed(2);

    const grandTotal = updatedOrderArr
      .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0)
      .toFixed(2);

    setPurchaseOrderItems(updatedOrderArr);

    setFormState((prev) => ({
      ...prev,
      grand_total: grandTotal,
    }));
  };

  const handleAddRow = () => {
    if (!isFormValid()) {
      showToast(
        "Please fill supplier details first before adding items",
        "ERROR",
      );
      return;
    }
    setPurchaseOrderItems((prev) => [
      ...prev,
      {
        medicine_id: "",
        medicine_name: "",
        pack: "",
        hsn_code: "",
        batch: "",
        expiry_date: "",
        quantity: "",
        free_quantity: "",
        cgst: 2.5,
        sgst: 2.5,
        rate: "",
        mrp: "",
        mfr: "",
        discount: "",
        amount: "",
        form_type: "",
        department_id: "9",
      },
    ]);
  };

  const handleDeleteRow = (index) => {
    setPurchaseOrderItems((prev) => prev.filter((_, i) => i !== index));
  };

  const isFormValid = () => {
    return !!(
      formState?.supplier_id &&
      formState?.bill_number &&
      formState?.bill_type &&
      formState?.purchase_date
    );
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formState.suppliers) newErrors.suppliers = "Supplier is required";
    if (!formState.purchase_date)
      newErrors.purchase_date = "Purchase date is required";
    if (!formState.bill_type) newErrors.bill_type = "Bill type is required";
    if (!formState.bill_number)
      newErrors.bill_number = "Bill number is required";

    purchaseOrderItems?.forEach((item, index) => {
      if (!item?.medicine_name)
        newErrors[`medicine_name_${index}`] = "Medicine is required";
      if (!item?.pack) newErrors[`pack_${index}`] = "Pack is required";
      if (!item?.batch) newErrors[`batch_${index}`] = "Batch is required";
      if (!item?.expiry_date)
        newErrors[`expiry_date_${index}`] = "Expiry date is required";
      if (item?.expiry_date && isDateInPast(item.expiry_date))
        newErrors[`expiry_date_${index}`] = "Expiry date cannot be in the past";
      if (!item?.quantity || item.quantity <= 0)
        newErrors[`quantity_${index}`] = "Quantity is required";
      if (!item?.cgst && item?.cgst < 0)
        newErrors[`cgst_${index}`] = "CGST is required";
      if (!item?.sgst && item?.sgst < 0)
        newErrors[`sgst_${index}`] = "SGST is required";
      if (item?.amount < 0) newErrors[`amount_${index}`] = "Amount is required";
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleKeyDown = (e, index, field) => {
    if (field === "medicine_name") {
      if (["ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        setHighlightedIndexMedicine((prevIndex) => {
          if (e.key === "ArrowDown")
            return prevIndex < filteredMedicines?.length - 1
              ? prevIndex + 1
              : 0;
          return prevIndex > 0 ? prevIndex - 1 : filteredMedicines?.length - 1;
        });
      } else if (e.key === "Tab" || e.key === "Enter") {
        if (!purchaseOrderItems[index]?.medicine_name) {
          setTimeout(() => document.getElementById(`submit`).focus(), 0);
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        const topOption = filteredMedicines?.[highlightedIndexMedicine];
        if (topOption) {
          const updatedItems = [...purchaseOrderItems];
          updatedItems[index] = {
            ...updatedItems[index],
            medicine_id: topOption?.id,
            medicine_name: topOption?.name,
            form_type: topOption?.form_type,
            flat_price: topOption?.flat_price,
          };
          setPurchaseOrderItems(updatedItems);
          setFilteredMedicines([]);
          setHighlightedIndexMedicine(0);
          setTimeout(
            () => document.getElementById(`pack-${index}`)?.focus(),
            0,
          );
        } else {
          setTimeout(
            () => document.getElementById(`pack-${index}`)?.focus(),
            0,
          );
        }
      }
    }
    const fieldMap = {
      pack: `hsnCode-${index}`,
      hsnCode: `batch-${index}`,
      batch: `expiry_date-${index}`,
      expiry_date: `quantity-${index}`,
      quantity: `free_quantity-${index}`,
      free_quantity: `cgst-${index}`,
      cgst: `sgst-${index}`,
      sgst: `rate-${index}`,
      rate: `mrp-${index}`,
      mrp: `mfr-${index}`,
      mfr: `discount-${index}`,
      discount: `amount-${index}`,
    };
    if (field in fieldMap && e.key === "Enter") {
      setTimeout(() => document.getElementById(fieldMap[field])?.focus(), 0);
    }
    if (field === "amount" && e.key === "Enter") {
      if (purchaseOrderItems?.length > index + 1) {
        setTimeout(
          () => document.getElementById(`medicine_name-${index + 1}`)?.focus(),
          100,
        );
      } else {
        handleAddRow();
        setTimeout(
          () => document.getElementById(`medicine_name-${index + 1}`)?.focus(),
          100,
        );
      }
    }
  };

  const handleCreateOrder = async () => {
    if (!validateForm()) return;
    setSubmitLoading(true);
    const payload = {
      purchase_order: {
        ...formState,
        department_id: "9",
        ...(purchaseOrderItems.length > 0 && {
          purchase_items_attributes: purchaseOrderItems.map(
            ({ medicine_name, ...rest }) => rest,
          ),
        }),
      },
    };
    try {
      const result = await generatePurchaseOrder(payload);
      if (result?.status?.status === "SUCCESS") {
        showToast("Purchase order created successfully", "SUCCESS");
        refetch?.();
        onClose();
      } else {
        showToast(
          result?.status?.message || result?.status?.status,
          result?.status?.status,
        );
      }
    } catch (error) {
      showToast(error.message, "ERROR");
    } finally {
      setSubmitLoading(false);
    }
  };
  const { data: medicineSaltList, isLoading: medicineSaltLoading } =
    UseGetAllMedicineSaltList();

  const medicineSaltOptions = useMemo(() => {
    return (
      medicineSaltList?.payload?.medicine_salt?.map(([id, name]) => ({
        value: id,
        label: name,
      })) || []
    );
  }, [medicineSaltList]);

  const fileAccept = ".jpg,.jpeg,.pdf,.png";

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ height: "90vh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        position="sticky"
        zIndex={10}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <AddCardIcon sx={{ color: "neutral.navyBlue", mb: "4px" }} />
          <Typography variant="title_medium" sx={{ fontSize: "20px" }}>
            {step === 1
              ? "Upload Purchase Order Image / PDF"
              : step === 2
                ? "Review New Medicines"
                : "Review & Confirm Purchase Order"}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {step > 1 && (
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                setStep(step === 3 && missingMedicines.length > 0 ? 2 : 1)
              }
              sx={{ textTransform: "capitalize", borderRadius: "8px" }}
            >
              ← Back
            </Button>
          )}
          <IconButton onClick={onClose}>
            <CloseIcon size="small" sx={{ color: "red" }} />
          </IconButton>
        </Box>
      </Box>

      {/* ── STEP 1: File upload ── */}
      {step === 1 && (
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <Box sx={{ gap: 2, display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                border: isDragging ? "2px dashed" : "2px dashed #E1E1E1",
                borderColor: isDragging ? "neutral.teal" : "#E1E1E1",
                borderRadius: "16px",
                p: 4,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: isDragging
                  ? "neutral.blueShadeBg"
                  : "neutral.backGround",
                transition: "0.3s",
                "&:hover": {
                  borderColor: "neutral.teal",
                  backgroundColor: "neutral.blueShadeBg",
                },
              }}
              onClick={() =>
                document.getElementById("purchase-order-upload").click()
              }
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <input
                id="purchase-order-upload"
                type="file"
                hidden
                accept={fileAccept}
                onChange={(e) => setFile(e.target.files[0])}
              />

              {!file ? (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Upload Purchase Order Image / PDF
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click to upload or drag & drop here
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PNG, JPG, JPEG, PDF up to 10MB
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {file.name}
                  </Typography>

                  {file.type.startsWith("image/") && (
                    <Box
                      component="img"
                      src={URL.createObjectURL(file)}
                      alt="preview"
                      sx={{
                        width: "100%",
                        maxHeight: 220,
                        objectFit: "contain",
                        borderRadius: "12px",
                        mb: 1,
                      }}
                    />
                  )}

                  {file.type === "application/pdf" && (
                    <Box
                      sx={{
                        width: "100%",
                        height: "50vh",
                        border: "1px solid #ddd",
                        borderRadius: "12px",
                        overflow: "hidden",
                        mb: 1,
                      }}
                    >
                      <iframe
                        src={URL.createObjectURL(file)}
                        title="PDF Preview"
                        width="100%"
                        height="100%"
                        style={{ border: "none" }}
                      />
                    </Box>
                  )}

                  <Button
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                  >
                    Remove
                  </Button>
                </>
              )}
            </Box>

            <Button
              onClick={handleUpload}
              disabled={uploadLoading || !file}
              sx={{ ...primaryBtnTeal, alignSelf: "flex-end" }}
            >
              {uploadLoading ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                "Scan & Continue →"
              )}
            </Button>
          </Box>
        </Box>
      )}

      {/* ── STEP 2: Review & Create Missing Medicines ── */}
      {step === 2 && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              overflowY: "auto",
              flex: 1,
              "&::-webkit-scrollbar": { display: "none" },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The following medicines were not found in the system. Please
              review and fill in the details before proceeding.
            </Typography>

            <TableContainer sx={{ width: "100%" }}>
              <Table sx={{ width: "100%" }}>
                <TableHead>
                  <TableRow sx={{ ...drTableGreen }}>
                    {["Medicine Name", "Form Type", "Salt / Composition"].map(
                      (label, i) => (
                        <TableCell
                          key={label}
                          sx={{
                            borderTopLeftRadius: i === 0 ? "20px" : undefined,
                            color: "neutral.white",
                            p: "8px 12px",
                          }}
                        >
                          <Typography
                            variant="body1_text_bold"
                            sx={{ color: "neutral.black" }}
                          >
                            {label}
                          </Typography>
                        </TableCell>
                      ),
                    )}
                    <TableCell
                      sx={{
                        borderTopRightRadius: "20px",
                        p: "4px 6px",
                        width: "36px",
                      }}
                    />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {missingMedicines.map((med, index) => (
                    <TableRow key={index}>
                      {/* Medicine Name */}
                      <TableCell sx={{ p: "6px 8px" }}>
                        <TextField
                          fullWidth
                          value={med.medicine_name}
                          onChange={(e) =>
                            handleMissingMedicineChange(
                              index,
                              "medicine_name",
                              e.target.value,
                            )
                          }
                          sx={{ ...textfieldTextBlueBr }}
                          placeholder="Medicine name"
                          error={!!errors[`medicine_name_${index}`]}
                          helperText={errors[`medicine_name_${index}`]}
                        />
                      </TableCell>

                      {/* Form Type */}
                      <TableCell sx={{ p: "6px 8px" }}>
                        <Autocomplete
                          clearIcon={null}
                          options={
                            formTypeList?.payload?.form_types?.filter(
                              Boolean,
                            ) || []
                          }
                          getOptionLabel={(option) => option?.label || ""}
                          value={
                            formTypeList?.payload?.form_types?.find(
                              (ft) => ft?.value === med.form_type,
                            ) || null
                          }
                          onChange={(e, value) =>
                            handleMissingMedicineChange(
                              index,
                              "form_type",
                              value?.value || "",
                            )
                          }
                          sx={{
                            ...textfieldTextBlueBorder,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "10px",
                              height: "40px",
                              padding: "9px !important",
                              "& input": {
                                padding: "8px",
                                fontSize: "14px",
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Form Type" />
                          )}
                          renderOption={(props, option) => (
                            <MenuItem {...props} key={option?.id}>
                              {option?.label}
                            </MenuItem>
                          )}
                          isOptionEqualToValue={(option, value) =>
                            option?.value === value?.value
                          }
                        />
                        {errors[`form_type_${index}`] && (
                          <Typography variant="caption" color="error">
                            {errors[`form_type_${index}`]}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Salt */}
                      <TableCell sx={{ p: "6px 8px", maxWidth: 260 }}>
                        <Box display="flex" alignItems="center" gap="4px">
                          <Box sx={{ flexGrow: 1 }}>
                            <CreatableSelect
                              placeholder="Medicine salt"
                              isClearable
                              isLoading={saltLoadingIndex === index}
                              options={[...medicineSaltOptions, ...extraSaltOptions].filter(
                                (opt) => !missingMedicines.some((m, i) => i !== index && m.medicine_salt === opt.label),
                              )}
                              value={
                                med?.medicine_salt
                                  ? { label: med.medicine_salt, value: med.medicine_salt }
                                  : null
                              }
                              onInputChange={(value, { action }) => {
                                if (action === "input-change") {
                                  saltInputRef.current[index] = value;
                                } else if (action === "set-value") {
                                  saltInputRef.current[index] = "";
                                }
                              }}
                              onBlur={() => {
                                const typed = saltInputRef.current[index];
                                if (typed && typed.trim()) {
                                  handleMissingMedicineChange(
                                    index,
                                    "medicine_salt",
                                    normalizeMedicineSalt(typed),
                                  );
                                }
                                saltInputRef.current[index] = "";
                              }}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              formatCreateLabel={(input) => `Create "${normalizeMedicineSalt(input)}"`}
                              formatOptionLabel={(option, { context }) =>
                                context === "menu" ? (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {option?.__isNew__ ? (
                                      <AddCircleIcon sx={{ fontSize: 18, color: "#1b7f3b" }} />
                                    ) : (
                                      <ScienceOutlinedIcon sx={{ fontSize: 18, color: "#7a869a" }} />
                                    )}
                                    <span>{option.label}</span>
                                  </Box>
                                ) : (
                                  option.label
                                )
                              }
                              onCreateOption={(inputValue) => handleCreateSaltAtIndex(inputValue, index)}
                              styles={{
                                ...selectStyles,
                                option: (base, state) => ({
                                  ...base,
                                  cursor: "pointer",
                                  ...(state.data?.__isNew__
                                    ? {
                                        color: "#1b7f3b",
                                        fontWeight: 600,
                                        backgroundColor: state.isFocused ? "#d6f0df" : "#eafaf0",
                                      }
                                    : {
                                        color: "#3a3a3a",
                                        backgroundColor: state.isFocused ? "#f0f7ff" : "#fff",
                                      }),
                                }),
                                menuPortal: (base) => ({ ...base, zIndex: 999999, borderRadius: "10px" }),
                                control: (base) => ({ ...base, borderRadius: "10px" }),
                                singleValue: (base) => ({ ...base, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", borderRadius: "10px" }),
                              }}
                              onChange={(selected) => {
                                const normalized = normalizeMedicineSalt(selected?.label || "");
                                handleMissingMedicineChange(index, "medicine_salt", normalized);
                              }}
                            />
                          </Box>
                          <Tooltip
                            title={missingMedicines[index]?.medicine_name?.trim() ? "Get medicine salt suggestion from AI" : "Enter medicine name first"}
                            placement="top"
                          >
                            <span>
                              <IconButton
                                onClick={() => handleAutoFillMissingSalt(index)}
                                disabled={!missingMedicines[index]?.medicine_name?.trim() || saltLoadingIndex === index}
                                size="small"
                                sx={{
                                  color: missingMedicines[index]?.medicine_name?.trim() ? "#2e7d32" : "action.disabled",
                                  "&:hover": { backgroundColor: "#e0f4f7", color: "#0097B2" },
                                  p: "4px",
                                }}
                              >
                                {saltLoadingIndex === index ? (
                                  <CircularProgress size={14} sx={{ color: "#2e7d32" }} />
                                ) : (
                                  <AutoFixHighIcon sx={{ fontSize: 16 }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                        {errors[`medicine_salt_${index}`] && (
                          <Typography variant="caption" color="error">
                            {errors[`medicine_salt_${index}`]}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Remove row */}
                      <TableCell
                        sx={{
                          p: "4px 6px",
                          textAlign: "center",
                          width: "36px",
                        }}
                      >
                        <IconButton
                          onClick={() => handleRemoveMissingMedicine(index)}
                          size="small"
                          sx={{ p: "2px" }}
                        >
                          <CloseIcon
                            sx={{
                              ...xBtn,
                              width: "20px",
                              height: "20px",
                              borderRadius: "50%",
                            }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box
            display="flex"
            justifyContent="flex-end"
            sx={{ zIndex: 10, pt: 1, pb: "5px" }}
          >
            <Button
              onClick={handleCreateMissingMedicines}
              variant="contained"
              sx={{
                ...primaryBtnTeal,
                width: "220px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              disabled={missingMedicinesLoading}
            >
              {missingMedicinesLoading ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                <Typography
                  textTransform="capitalize"
                  color="neutral.white"
                  variant="title1_medium"
                >
                  Save & Continue →
                </Typography>
              )}
            </Button>
          </Box>
        </Box>
      )}

      {/* ── STEP 3: Review & Submit Purchase Order ── */}
      {step === 3 && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            overflow: "hidden",
          }}
        >
          {/* Scrollable content */}
          <Box
            sx={{
              overflowY: "auto",
              flex: 1,
              "&::-webkit-scrollbar": { display: "none" },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            {/* ── Top form fields ── */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {/* Supplier */}
              <Grid item sm={4}>
                <Autocomplete
                  onFocus={() => setFocus(true)}
                  onBlur={() => setFocus(false)}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "8px",
                      border: "neutral.blackBorder",
                    },
                    "& .MuiInputBase-input": { padding: "2px 8px" },
                    "& .MuiAutocomplete-inputRoot": { padding: "2px 8px" },
                    borderRadius: "8px",
                    backgroundColor: "neutral.white",
                    "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.12)" },
                  }}
                  options={fieldOptions?.supplierList || []}
                  getOptionLabel={(option) => option.name || ""}
                  // Use selectedSupplier (set by AI auto-match or manual pick)
                  value={selectedSupplier}
                  onChange={(e, newValue) => {
                    setSelectedSupplier(newValue || null);
                    handleFormChange({
                      target: { name: "suppliers", value: newValue?.id || "" },
                    });
                    handleFormChange({
                      target: {
                        name: "supplier_id",
                        value: newValue?.id || "",
                      },
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Supplier"
                      onChange={(e) => setSupplierInput(e?.target?.value)}
                      error={!!errors.suppliers}
                      // helperText={
                      //   // Show a hint when AI returned a supplier name but no match found yet
                      //   !selectedSupplier && aiSupplierName
                      //     ? `Searching for "${aiSupplierName}"…`
                      //     : errors.suppliers || ""
                      // }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {supplierListLoading ? (
                              <CircularProgress color="inherit" size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <MenuItem
                      sx={menuItemStyle}
                      {...props}
                      key={option.id}
                      value={option.id}
                    >
                      {option.name}
                    </MenuItem>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                />
                {errors.suppliers && !(!selectedSupplier && aiSupplierName) && (
                  <Typography color="error">{errors.suppliers}</Typography>
                )}
              </Grid>

              {/* Purchase Date */}
              <Grid item sm={2}>
                <FormControl variant="outlined" sx={{ width: "100%" }}>
                  <TextField
                    onFocus={() => setFocus(true)}
                    onBlur={() => setFocus(false)}
                    fullWidth
                    required
                    onChange={handleFormChange}
                    value={
                      formState?.purchase_date ||
                      new Date().toISOString().split("T")[0]
                    }
                    type="date"
                    name="purchase_date"
                    sx={autoFocusOffWithOutBorder}
                    error={!!errors.purchase_date}
                    helperText={errors.purchase_date}
                  />
                </FormControl>
              </Grid>

              {/* Bill Type */}
              <Grid item sm={2}>
                <FormControl fullWidth variant="outlined">
                  <Select
                    onFocus={() => setFocus(true)}
                    onBlur={() => setFocus(false)}
                    error={!!errors.bill_type}
                    required
                    fullWidth
                    sx={selectField}
                    value={formState?.bill_type}
                    onChange={handleFormChange}
                    name="bill_type"
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected)
                        return (
                          <span style={{ color: "text.grey", opacity: "0.5" }}>
                            Bill Type
                          </span>
                        );
                      return selected === "credit"
                        ? "Credit"
                        : selected === "cash"
                          ? "Cash"
                          : "";
                    }}
                  >
                    <MenuItem value="credit" sx={menuItemStyle}>
                      Credit
                    </MenuItem>
                    <MenuItem value="cash" sx={menuItemStyle}>
                      Cash
                    </MenuItem>
                  </Select>
                  {errors.bill_type && (
                    <Typography color="error">{errors.bill_type}</Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Bill Number */}
              <Grid item sm={2}>
                <TextField
                  onFocus={() => setFocus(true)}
                  onBlur={() => setFocus(false)}
                  fullWidth
                  onChange={handleFormChange}
                  value={formState?.bill_number}
                  name="bill_number"
                  placeholder="Bill number"
                  required
                  variant="outlined"
                  sx={{ ...autoFocusOffWithOutBorder }}
                  error={!!errors.bill_number}
                  helperText={errors.bill_number}
                />
              </Grid>

              {/* Grand Total */}
              <Grid item sm={2}>
                <TextField
                  onFocus={() => setFocus(true)}
                  onBlur={() => setFocus(false)}
                  fullWidth
                  onChange={handleFormChange}
                  value={formState?.grand_total}
                  name="grand_total"
                  placeholder="Grand Total"
                  type="number"
                  required
                  variant="outlined"
                  sx={{ ...autoFocusOffWithOutBorder }}
                />
              </Grid>
            </Grid>

            {/* ── Medicine table ── */}
            <Grid item xs={12}>
              <TableContainer sx={{ mt: 2, width: "100%" }}>
                <Table sx={{ width: "100%" }}>
                  <TableHead>
                    <TableRow sx={{ ...drTableGreen, textAlign: "center" }}>
                      {[
                        { label: "Medicine", minWidth: 200 },
                        { label: "Form Type", minWidth: 90 },
                        { label: "Pack", minWidth: 120 },
                        { label: "HSN Code", minWidth: 90 },
                        { label: "Batch", minWidth: 90 },
                        { label: "Expiry", minWidth: 110 },
                        { label: "Quantity", minWidth: 90 },
                        { label: "Free Qty", minWidth: 90 },
                        { label: "CGST", minWidth: 80 },
                        { label: "SGST", minWidth: 80 },
                        { label: "Rate", minWidth: 80 },
                        { label: "MRP", minWidth: 80 },
                        { label: "MFR", minWidth: 80 },
                        { label: "Discount", minWidth: 80 },
                        { label: "Amount", minWidth: 90 },
                      ].map((col, i) => (
                        <TableCell
                          key={col.label}
                          sx={{
                            borderTopLeftRadius: i === 0 ? "20px" : undefined,
                            color: "neutral.white",
                            p: "2px",
                            textAlign: "center",
                            minWidth: col.minWidth,
                          }}
                        >
                          <Typography
                            variant="body1_text_bold"
                            sx={{ color: "neutral.black" }}
                          >
                            {col.label}
                          </Typography>
                        </TableCell>
                      ))}
                      <TableCell
                        sx={{
                          p: "2px",
                          textAlign: "center",
                          borderTopRightRadius: "20px",
                        }}
                      >
                        <IconButton
                          onClick={handleAddRow}
                          sx={{
                            ...orangeButton,
                            marginLeft: "7px",
                            marginY: "2px",
                          }}
                        >
                          <AddIcon
                            sx={{
                              color: "neutral.white",
                              width: "30px",
                              height: "32px",
                              padding: "0px",
                            }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {purchaseOrderItems?.map((item, index) => (
                      <TableRow
                        key={index}
                        sx={{ padding: "0px", margin: "0px" }}
                      >
                        {/* Medicine Name */}
                        <TableCell
                          sx={{
                            p: "2px",
                            textAlign: "center",
                            minWidth: "200px",
                          }}
                        >
                          <Autocomplete
                            options={filteredMedicines || []}
                            freeSolo
                            clearOnEscape={false}
                            open={openMedicineList === index}
                            onOpen={() => setOpenMedicines(index)}
                            onClose={() => setOpenMedicines(false)}
                            id={`medicine_name-${index}`}
                            inputValue={
                              item?.medicine_name !== null
                                ? item?.medicine_name
                                : ""
                            }
                            onInputChange={(e, value) =>
                              handleChange(index, "medicine_name", value)
                            }
                            onChange={(e, value) => {
                              if (value) {
                                const updatedItems = [...purchaseOrderItems];
                                updatedItems[index] = {
                                  ...updatedItems[index],
                                  medicine_id: value?.id,
                                  form_type: value?.form_type,
                                  flat_price: value?.flat_price,
                                };
                                setPurchaseOrderItems(updatedItems);
                                setTimeout(
                                  () =>
                                    document
                                      .getElementById(`pack-${index}`)
                                      ?.focus(),
                                  0,
                                );
                              }
                            }}
                            sx={textfieldTextBlueBorderPharmacy}
                            PopperComponent={(props) => (
                              <Grid item lg={8}>
                                <Popper
                                  {...props}
                                  placement="bottom-start"
                                  style={menuItemStyle}
                                  modifiers={[
                                    {
                                      name: "preventOverflow",
                                      options: { boundary: "block" },
                                    },
                                  ]}
                                >
                                  <div
                                    style={{
                                      maxHeight: 300,
                                      overflowY: "auto",
                                      scrollbarWidth: "none",
                                      maxWidth: "320px",
                                      minWidth: "310px",
                                    }}
                                  >
                                    {props.children}
                                  </div>
                                </Popper>
                              </Grid>
                            )}
                            getOptionLabel={(option) => option?.name || ""}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                onChange={(e) =>
                                  setMedicineSearchInput(e?.target?.value)
                                }
                                placeholder="Medicine"
                                error={!!errors[`medicine_name_${index}`]}
                                helperText={
                                  errors[`medicine_name_${index}`] || ""
                                }
                                onKeyDown={(e) =>
                                  handleKeyDown(e, index, "medicine_name")
                                }
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {medicineLoading ? (
                                        <CircularProgress
                                          color="inherit"
                                          size={18}
                                        />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            renderOption={(
                              props,
                              option,
                              { index: optIdx },
                            ) => (
                              <MenuItem
                                sx={{
                                  ...menuItemStyle,
                                  "&:hover": {
                                    backgroundColor: "#0067FF40 !important",
                                    borderRadius: "16px",
                                    color: "black !important",
                                  },
                                  ...(highlightedIndexMedicine === optIdx && {
                                    backgroundColor: "#0067FF40 !important",
                                    color: "black !important",
                                    borderRadius: "16px",
                                  }),
                                }}
                                {...props}
                                key={option?.id}
                              >
                                {option?.name}
                              </MenuItem>
                            )}
                          />
                        </TableCell>

                        {/* Form Type */}
                        <TableCell
                          sx={{
                            p: "2px",
                            textAlign: "center",
                            minWidth: "90px",
                          }}
                        >
                          <Autocomplete
                            clearOnEscape={false}
                            clearIcon={null}
                            id={`form-type-${index}`}
                            onKeyDown={(e) =>
                              handleKeyDown(e, index, "form_type")
                            }
                            sx={{
                              ...textfieldTextBlueBorder,
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                                height: "40px",
                                padding: "9px !important",
                                "& input": { padding: "8px", fontSize: "14px" },
                              },
                            }}
                            options={
                              formTypeList?.payload?.form_types?.filter(
                                Boolean,
                              ) || []
                            }
                            getOptionLabel={(option) => option?.label || ""}
                            value={
                              formTypeList?.payload?.form_types?.find(
                                (ft) => ft?.value === item?.form_type,
                              ) || null
                            }
                            onChange={(e, value) => {
                              handleChange(
                                index,
                                "form_type",
                                value?.value || "",
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Form Type"
                                error={!!errors[`form_type_${index}`]}
                                helperText={errors[`form_type_${index}`] || ""}
                              />
                            )}
                            renderOption={(props, option) => (
                              <MenuItem {...props} key={option?.id}>
                                {option?.label}
                              </MenuItem>
                            )}
                            isOptionEqualToValue={(option, value) =>
                              option?.value === value?.value
                            }
                          />
                          {/* <TextField
                            placeholder="Form Type"

                            value={item?.form_type || ""}
                            id={`form-type-${index}`}
                            sx={{ ...textfieldTextBlueBr }}
                          /> */}
                        </TableCell>

                        {/* Pack */}
                        <TableCell
                          sx={{
                            p: "2px",
                            textAlign: "center",
                            minWidth: "120px",
                          }}
                        >
                          <Autocomplete
                            clearOnEscape={false}
                            clearIcon={null}
                            id={`pack-${index}`}
                            onKeyDown={(e) => handleKeyDown(e, index, "pack")}
                            sx={{
                              ...textfieldTextBlueBorder,
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                                height: "40px",
                                padding: "9px !important",
                                "& input": { padding: "8px", fontSize: "14px" },
                              },
                            }}
                            options={
                              fieldOptions?.packList?.filter(Boolean) || []
                            }
                            getOptionLabel={(option) => option || ""}
                            value={item?.pack || ""}
                            onChange={(e, value) => {
                              if (value)
                                handleChange(index, "pack", value || "");
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Pack"
                                error={!!errors[`pack_${index}`]}
                                helperText={errors[`pack_${index}`] || ""}
                              />
                            )}
                            renderOption={(props, option) => (
                              <MenuItem {...props} key={option}>
                                {option}
                              </MenuItem>
                            )}
                            isOptionEqualToValue={(option, value) =>
                              option === value
                            }
                          />
                        </TableCell>

                        {/* HSN Code */}
                        <TableCell
                          sx={{
                            p: "2px",
                            textAlign: "center",
                            minWidth: "90px",
                          }}
                        >
                          <TextField
                            placeholder="HSN code"
                            value={item?.hsn_code || ""}
                            id={`hsnCode-${index}`}
                            onChange={(e) =>
                              handleChange(index, "hsn_code", e.target.value)
                            }
                            onKeyDown={(e) =>
                              handleKeyDown(e, index, "hsnCode")
                            }
                            sx={{ ...textfieldTextBlueBr }}
                          />
                        </TableCell>

                        {/* Batch */}
                        <TableCell
                          sx={{
                            p: "2px",
                            textAlign: "center",
                            minWidth: "90px",
                          }}
                        >
                          <TextField
                            placeholder="Batch"
                            sx={textfieldTextBlueBr}
                            value={item?.batch || ""}
                            id={`batch-${index}`}
                            onChange={(e) =>
                              handleChange(index, "batch", e.target.value)
                            }
                            onKeyDown={(e) => handleKeyDown(e, index, "batch")}
                            error={!!errors[`batch_${index}`]}
                            helperText={errors[`batch_${index}`] || ""}
                          />
                        </TableCell>

                        {/* Expiry Date */}
                        <TableCell sx={{ p: "2px", textAlign: "center" }}>
                          <TextField
                            placeholder="EXP"
                            sx={textfieldTextBlueBr}
                            value={item?.expiry_date || ""}
                            inputProps={{
                              min: new Date().toISOString().split("T")[0],
                            }}
                            id={`expiry_date-${index}`}
                            onChange={(e) =>
                              handleChange(index, "expiry_date", e.target.value)
                            }
                            onKeyDown={(e) =>
                              handleKeyDown(e, index, "expiry_date")
                            }
                            type="date"
                            error={!!errors[`expiry_date_${index}`]}
                            helperText={errors[`expiry_date_${index}`] || ""}
                          />
                        </TableCell>

                        {/* Quantity */}
                        <TableCell
                          sx={{
                            p: "2px",
                            textAlign: "center",
                            minWidth: "90px",
                          }}
                        >
                          <TextField
                            value={item?.quantity || ""}
                            id={`quantity-${index}`}
                            onChange={(e) =>
                              handleChange(index, "quantity", e.target.value)
                            }
                            onKeyDown={(e) =>
                              handleKeyDown(e, index, "quantity")
                            }
                            placeholder="Quantity"
                            sx={textfieldTextBlueBr}
                            type="number"
                            error={!!errors[`quantity_${index}`]}
                            helperText={errors[`quantity_${index}`] || ""}
                          />
                        </TableCell>

                        {/* Free Quantity */}
                        <TableCell
                          sx={{
                            p: "2px",
                            textAlign: "center",
                            minWidth: "90px",
                          }}
                        >
                          <TextField
                            value={item?.free_quantity || ""}
                            id={`free_quantity-${index}`}
                            onChange={(e) =>
                              handleChange(
                                index,
                                "free_quantity",
                                e.target.value,
                              )
                            }
                            onKeyDown={(e) =>
                              handleKeyDown(e, index, "free_quantity")
                            }
                            placeholder="Free Qty"
                            sx={textfieldTextBlueBr}
                            type="number"
                          />
                        </TableCell>

                        {/* CGST */}
                        <TableCell
                          sx={{
                            p: "2px",
                            textAlign: "center",
                            minWidth: "80px",
                          }}
                        >
                          <TextField
                            value={item?.cgst || ""}
                            onChange={(e) =>
                              handleChange(index, "cgst", e.target.value)
                            }
                            id={`cgst-${index}`}
                            onKeyDown={(e) => handleKeyDown(e, index, "cgst")}
                            placeholder="CGST"
                            sx={textfieldTextBlueBr}
                            type="number"
                            error={!!errors[`cgst_${index}`]}
                            helperText={errors[`cgst_${index}`] || ""}
                          />
                        </TableCell>

                        {/* SGST */}
                        <TableCell
                          sx={{
                            p: "2px",
                            minWidth: "80px",
                            textAlign: "center",
                          }}
                        >
                          <TextField
                            value={item?.sgst || ""}
                            onChange={(e) =>
                              handleChange(index, "sgst", e.target.value)
                            }
                            placeholder="SGST"
                            id={`sgst-${index}`}
                            onKeyDown={(e) => handleKeyDown(e, index, "sgst")}
                            sx={textfieldTextBlueBr}
                            type="number"
                            error={!!errors[`sgst_${index}`]}
                            helperText={errors[`sgst_${index}`] || ""}
                          />
                        </TableCell>

                        {/* Rate */}
                        <TableCell
                          sx={{
                            p: "2px",
                            minWidth: "80px",
                            textAlign: "center",
                          }}
                        >
                          <TextField
                            value={item?.rate || ""}
                            onChange={(e) =>
                              handleChange(index, "rate", e.target.value)
                            }
                            placeholder="Rate"
                            id={`rate-${index}`}
                            onKeyDown={(e) => handleKeyDown(e, index, "rate")}
                            sx={textfieldTextBlueBr}
                            type="number"
                          />
                        </TableCell>

                        {/* MRP */}
                        <TableCell
                          sx={{
                            p: "2px",
                            textAlign: "center",
                            minWidth: "80px",
                          }}
                        >
                          <TextField
                            value={item?.mrp || ""}
                            onChange={(e) =>
                              handleChange(index, "mrp", e.target.value)
                            }
                            placeholder="MRP"
                            sx={textfieldTextBlueBr}
                            id={`mrp-${index}`}
                            onKeyDown={(e) => handleKeyDown(e, index, "mrp")}
                            type="number"
                          />
                        </TableCell>

                        {/* MFR */}
                        <TableCell
                          sx={{
                            p: "2px",
                            textAlign: "center",
                            minWidth: "80px",
                          }}
                        >
                          <TextField
                            value={item?.mfr || ""}
                            onChange={(e) =>
                              handleChange(index, "mfr", e.target.value)
                            }
                            placeholder="MFR"
                            id={`mfr-${index}`}
                            onKeyDown={(e) => handleKeyDown(e, index, "mfr")}
                            sx={textfieldTextBlueBr}
                          />
                        </TableCell>

                        {/* Discount */}
                        <TableCell
                          sx={{
                            p: "2px",
                            textAlign: "center",
                            minWidth: "80px",
                          }}
                        >
                          <TextField
                            value={item?.discount || ""}
                            onChange={(e) =>
                              handleChange(index, "discount", e.target.value)
                            }
                            id={`discount-${index}`}
                            onKeyDown={(e) =>
                              handleKeyDown(e, index, "discount")
                            }
                            placeholder="Discount"
                            sx={textfieldTextBlueBr}
                          />
                        </TableCell>

                        {/* Amount */}
                        <TableCell
                          sx={{
                            p: "2px",
                            textAlign: "center",
                            minWidth: "90px",
                          }}
                        >
                          <TextField
                            value={item?.amount || ""}
                            onChange={(e) =>
                              handleChange(index, "amount", e.target.value)
                            }
                            id={`amount-${index}`}
                            onKeyDown={(e) => handleKeyDown(e, index, "amount")}
                            placeholder="Amount"
                            sx={textfieldTextBlueBr}
                            type="number"
                            error={!!errors[`amount_${index}`]}
                            helperText={errors[`amount_${index}`] || ""}
                            inputProps={{ readOnly: !!item?.flat_price }}
                          />
                          {item?.flat_price && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "orange",
                                mt: "2px",
                                display: "block",
                              }}
                            >
                              Flat Price: ₹{item?.flat_price}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Delete row */}
                        <TableCell
                          sx={{
                            p: "2px",
                            textAlign: "center",
                            minWidth: "70px",
                          }}
                        >
                          <IconButton
                            sx={{ marginLeft: "2px", marginBottom: "7px" }}
                            onClick={() => handleDeleteRow(index)}
                          >
                            <CloseIcon
                              sx={{
                                ...xBtn,
                                width: "28px",
                                height: "28px",
                                borderRadius: "50%",
                              }}
                            />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

          </Box>

          {/* ── Footer: Create Order button ── */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ zIndex: 10, pt: 1, pb: "5px" }}
          >
            {purchaseOrderItems.length > 0 && (() => {
              const totals = purchaseOrderItems.reduce(
                (acc, item) => {
                  const rate = parseFloat(item.rate) || 0;
                  const quantity = parseFloat(item.quantity) || 0;
                  const discount = parseFloat(item.discount) || 0;
                  const cgst = parseFloat(item.cgst) || 0;
                  const sgst = parseFloat(item.sgst) || 0;
                  const taxable = Math.max(rate * quantity - discount, 0);
                  acc.subtotal += rate * quantity;
                  acc.discount += discount;
                  acc.taxable += taxable;
                  acc.cgst += (taxable * cgst) / 100;
                  acc.sgst += (taxable * sgst) / 100;
                  return acc;
                },
                { subtotal: 0, discount: 0, taxable: 0, cgst: 0, sgst: 0 },
              );
              const totalGST = totals.cgst + totals.sgst;
              const grandTotal = Math.round(totals.taxable + totalGST);
              return (
                <Box sx={{ display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap" }}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="caption" color="text.secondary">
                      Sub Total
                    </Typography>
                    <Typography variant="body1_text_bold" display="block">
                      ₹{totals.subtotal.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="caption" color="text.secondary">
                      Total Discount
                    </Typography>
                    <Typography variant="body1_text_bold" display="block" sx={{ color: "error.main" }}>
                      −₹{totals.discount.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="caption" color="text.secondary">
                      Taxable Amount
                    </Typography>
                    <Typography variant="body1_text_bold" display="block">
                      ₹{totals.taxable.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="caption" color="text.secondary">
                      CGST
                    </Typography>
                    <Typography variant="body1_text_bold" display="block">
                      ₹{totals.cgst.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="caption" color="text.secondary">
                      SGST
                    </Typography>
                    <Typography variant="body1_text_bold" display="block">
                      ₹{totals.sgst.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      textAlign: "center",
                      borderLeft: "2px solid",
                      borderColor: "divider",
                      pl: 2,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Total GST
                    </Typography>
                    <Typography
                      variant="body1_text_bold"
                      display="block"
                      sx={{ color: "neutral.navyBlue" }}
                    >
                      ₹{totalGST.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      textAlign: "center",
                      borderLeft: "2px solid",
                      borderColor: "divider",
                      pl: 2,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Grand Total
                    </Typography>
                    <Typography
                      variant="body1_text_bold"
                      display="block"
                      sx={{ color: "neutral.navyBlue" }}
                    >
                      ₹{grandTotal}
                    </Typography>
                  </Box>
                </Box>
              );
            })()}
            <Button
              onClick={handleCreateOrder}
              variant="contained"
              id="submit"
              sx={{
                ...primaryBtnTeal,
                width: "195px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              disabled={submitLoading}
            >
              {submitLoading ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                <Typography
                  textTransform="capitalize"
                  color="neutral.white"
                  variant="title1_medium"
                >
                  Create Order
                </Typography>
              )}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AddPurchaseOrderFileScan;
